import type { ReactNode } from "react";
import { Icon } from "../icons/Icon";

/** The one design for every numbered how-to in the app (the send-and-sections round, 2026-10-23: a kit rule). Two forms of one list:
 *  `wizard`, the mail set-up's guided steps (`StepCard`: done ones fold, the current one is open, later ones show what is coming), and `open`,
 *  a static how-to whose steps are all shown at once as plain numbered regions without borders (`Step`; the app dialogs: the founder's "too
 *  many boxes"). Each step is a region named "Step <n>: <title>". */
export function Steps({ form = "wizard", label, children }: { form?: "wizard" | "open"; /** what the steps set up, for a screen reader */ label?: string; children?: ReactNode }) {
  return <div className={`ob-steps ob-steps--${form}`} aria-label={label}>{children}</div>;
}

/** One step of the open form: its number, a short title (sentence case) and what to do, everything shown at once. */
export function Step({ n, title, children }: { n: number; title: string; children?: ReactNode }) {
  return (
    <section className="ob-step ob-step--open" aria-label={`Step ${n}: ${title}`}>
      <header className="ob-step__head"><span className="ob-step__n">{n}</span><h3 className="ob-step__title">{title}</h3></header>
      <div className="ob-step__body">{children}</div>
    </section>
  );
}

/** One numbered step of a guided setup. Every step stays on screen: done ones fold to a summary, the current one is open, later ones show what is coming. */
export function StepCard({ n, title, state, summary, hint, onEdit, children }: {
  n: number; title: string; state: "done" | "current" | "todo";
  /** done: what was decided, in one line */ summary?: ReactNode; /** todo: what this step will ask, in one line */ hint?: string;
  onEdit?: () => void; children?: ReactNode;
}) {
  return (
    <section className={`ob-step ob-step--${state}`} aria-label={`Step ${n}: ${title}`} aria-current={state === "current" ? "step" : undefined}>
      <header className="ob-step__head">
        <span className="ob-step__n">{state === "done" ? <Icon name="check" size="sm" /> : n}</span>
        <h2 className="ob-step__title">{title}</h2>
        {state === "done" && onEdit && <button type="button" className="ob-link ob-step__edit" onClick={onEdit}>Change</button>}
      </header>
      {state === "done" && summary && <div className="ob-step__summary">{summary}</div>}
      {state === "todo" && hint && <div className="ob-step__summary">{hint}</div>}
      {state === "current" && <div className="ob-step__body">{children}</div>}
    </section>
  );
}
